/**
 * Onboarding Welcome 动效常量（GSAP masterTimeline）
 * @see Figma 88215:37171 · accent 渐变 42903:14332
 */

export const ONBOARDING_WELCOME_ANIM = {
  staggerMs: 120,
  drawerEnterDelayMs: 300,
  slideY: 8,
  textDurationMs: 400,
  counterDurationMs: 750,
  /** GSAP `-= durationVal * 1.2` */
  counterLeadMs: 480,
  accentFadeDurationMs: 500,
  /** gradient `"-= 0.35"` */
  accentOverlapMs: 350,
  buttonDurationMs: 600,
  /** main-action `"-= 0.25"` */
  buttonOverlapMs: 250,
} as const;

export const ONBOARDING_WELCOME_STREAM_CHUNK_COUNT = 5;

/** @deprecated */
export const ONBOARDING_WELCOME_TEXT_BLOCK_COUNT =
  ONBOARDING_WELCOME_STREAM_CHUNK_COUNT;

export const getTextCascadeEndMs = (chunkCount: number): number =>
  (chunkCount - 1) * ONBOARDING_WELCOME_ANIM.staggerMs +
  ONBOARDING_WELCOME_ANIM.textDurationMs;

export const getCounterStartMs = (chunkCount: number): number =>
  getTextCascadeEndMs(chunkCount) - ONBOARDING_WELCOME_ANIM.counterLeadMs;

export const getAccentFadeStartMs = (chunkCount: number): number =>
  getCounterStartMs(chunkCount) +
  ONBOARDING_WELCOME_ANIM.counterDurationMs -
  ONBOARDING_WELCOME_ANIM.accentOverlapMs;

export const getButtonDelayMs = (chunkCount: number): number =>
  getAccentFadeStartMs(chunkCount) +
  ONBOARDING_WELCOME_ANIM.accentFadeDurationMs -
  ONBOARDING_WELCOME_ANIM.buttonOverlapMs;

/**
 * Figma accent4 终态纯色
 * @see globals.css `.onboarding-welcome-accent-phrase`
 */
export const ONBOARDING_WELCOME_ACCENT_COLOR =
  "var(--edgen-content_accent4_primary)";

/** @deprecated 使用 ONBOARDING_WELCOME_ACCENT_COLOR */
export const ONBOARDING_WELCOME_GRADIENT_TEXT = ONBOARDING_WELCOME_ACCENT_COLOR;

/** GSAP power2.out */
export const easePower2Out = (t: number): number => 1 - (1 - t) ** 2;

/** GSAP sine.out（渐变层） */
export const easeOutSine = (t: number): number => Math.sin((t * Math.PI) / 2);

const clamp01 = (t: number): number => Math.min(1, Math.max(0, t));

export interface WaveChunkMotion {
  opacity: number;
  transform: string;
}

const hiddenWave = (): WaveChunkMotion => ({
  opacity: 0,
  transform: `translateY(${ONBOARDING_WELCOME_ANIM.slideY}px)`,
});

export const getWaveChunkMotionAt = (
  elapsedMs: number,
  order: number,
): WaveChunkMotion => {
  const start = order * ONBOARDING_WELCOME_ANIM.staggerMs;
  if (elapsedMs < start) {
    return hiddenWave();
  }

  const progress = clamp01(
    (elapsedMs - start) / ONBOARDING_WELCOME_ANIM.textDurationMs,
  );
  const eased = easePower2Out(progress);

  return {
    opacity: eased,
    transform: `translateY(${ONBOARDING_WELCOME_ANIM.slideY * (1 - eased)}px)`,
  };
};

export const getButtonMotionAt = (
  elapsedMs: number,
  chunkCount: number,
): WaveChunkMotion => {
  const start = getButtonDelayMs(chunkCount);
  if (elapsedMs < start) {
    return hiddenWave();
  }

  const progress = clamp01(
    (elapsedMs - start) / ONBOARDING_WELCOME_ANIM.buttonDurationMs,
  );
  const eased = easePower2Out(progress);

  return {
    opacity: eased,
    transform: `translateY(${ONBOARDING_WELCOME_ANIM.slideY * (1 - eased)}px)`,
  };
};

export const getTimelineEndMs = (chunkCount: number): number =>
  getButtonDelayMs(chunkCount) + ONBOARDING_WELCOME_ANIM.buttonDurationMs + 100;

/** @deprecated */
export const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

/** @deprecated */
export const ONBOARDING_WELCOME_ITEMS_ACCENT_GRADIENT =
  ONBOARDING_WELCOME_GRADIENT_TEXT;
